import { useQuery } from "@tanstack/react-query";
import { toastError, toastSuccess } from "helpers";
import { createLogger } from "logging";
import { SuggestedAgendaItemModel } from "models/SuggestedAgendaItemModel";
import { useApi, useMeeting, useTranslations } from "providers";
import { useSuggestedAgendaItem } from "providers";
import React, { PropsWithChildren, useCallback, useMemo, useState } from "react";

import { SuggestedAgendaItemsPanel } from "./components/SuggestedAgendaItemsPanel";
import { SuggestedAgendaItemPanelContext } from "./SuggestedAgendaItemPanelContext";

const logger = createLogger("SuggestedAgendaItemPanelProvider");

const filterItem = (x: SuggestedAgendaItemModel, text: string) => {
  const agenaItemTitle = x.sourceAgendaItem.title.toLowerCase();
  const meetingTitle = x.sourceMeeting.title.toLowerCase();
  return agenaItemTitle.includes(text.toLowerCase()) || meetingTitle.includes(text.toLowerCase());
};

export const SuggestedAgendaItemPanelProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const i18n = useTranslations();
  const { reloadMeeting } = useMeeting();

  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [submitMessage, setSubmitMessage] = useState<string>();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const { suggestedAgendaItemsApi } = useApi();
  const { meetingId } = useMeeting();
  const { suggestedAgendaItemsCount } = useSuggestedAgendaItem();

  // prettier-ignore
  const queryResult = useQuery({
    queryKey: [`Meeting|${meetingId}|SuggestedAgendaItems`],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const data = await suggestedAgendaItemsApi.getItems(Number(meetingId), signal);
      logger.trace("Loaded:", data);
      return data;
    },
    enabled: !!meetingId && isOpen,
  });

  const isLoading = queryResult.isFetching;
  const items = useMemo(() => queryResult.data || [], [queryResult.data]);
  const isForm = !isLoading && !isSubmitting && !isDeleting;

  const filteredItems = useMemo(() => {
    let r = filterText ? items?.filter((x) => filterItem(x, filterText)) : items;
    r = r || [];
    logger.trace(`filteredItems(useMemo): `, filterText, r);
    return r;
  }, [filterText, items]);

  const openPanel = useCallback(() => {
    logger.debug("openPanel(): ");
    setIsOpen(true);
    setFilterText("");
    setSubmitMessage(undefined);
    setSelectedIds(new Set());
  }, []);

  const closePanel = useCallback(() => {
    logger.debug("closePanel(): ");
    setIsOpen(false);
    suggestedAgendaItemsCount.refetch();
  }, [suggestedAgendaItemsCount]);

  const onItemChange = useCallback((id: number, selected: boolean) => {
    logger.debug("onItemChange(): ", id, selected);

    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  }, []);

  const onDelete = useCallback(() => {
    if (!meetingId) return;
    setIsDeleting(true);

    const ids = Array.from(selectedIds);
    logger.debug("onDelete(): ", ids, meetingId);

    suggestedAgendaItemsApi
      .delete(ids)
      .then(() => {
        setSubmitMessage(i18n.SuggestedAgendaItems_Delete_Success);
        setSelectedIds(new Set());
        queryResult.refetch();
        suggestedAgendaItemsCount.refetch();
        setIsDeleting(false);
        toastSuccess(i18n.SuggestedAgendaItems_Delete_Success);
      })
      .catch((error) => {
        logger.error("Delete error:", error);
        setIsDeleting(false);
        toastError("Failed to delete items", error);
      });
  }, [meetingId, selectedIds, suggestedAgendaItemsApi, queryResult, suggestedAgendaItemsCount, i18n]);

  const onMove = useCallback(() => {
    if (!meetingId) return;
    setIsSubmitting(true);
    const ids = Array.from(selectedIds);
    logger.debug("onMove(): ", ids, meetingId);

    suggestedAgendaItemsApi
      .moveTo(ids, meetingId)
      .then(() => {
        setSubmitMessage(i18n.SuggestedAgendaItems_Move_Success);
        setSelectedIds(new Set());
        queryResult.refetch();
        suggestedAgendaItemsCount.refetch();

        toastSuccess(i18n.SuggestedAgendaItems_Move_Success);
      })
      .catch((error) => {
        toastError("Failed to move items", error);
      })
      .finally(() => {
        setIsSubmitting(false);
        reloadMeeting();
      });
  }, [meetingId, selectedIds, suggestedAgendaItemsApi, queryResult, suggestedAgendaItemsCount, i18n, reloadMeeting]);

  const contextValue = useMemo(
    () => ({
      isOpen,
      isLoading,
      isSubmitting,
      isDeleting,
      isForm,
      filterText,
      submitMessage,
      selectedIds,
      items,
      filteredItems,
      setFilterText,
      openPanel,
      closePanel,
      onItemChange,
      onMove,
      onDelete,
    }),
    [
      isOpen,
      isLoading,
      isSubmitting,
      isDeleting,
      isForm,
      filterText,
      submitMessage,
      selectedIds,
      items,
      filteredItems,
      setFilterText,
      openPanel,
      closePanel,
      onItemChange,
      onMove,
      onDelete,
    ]
  );

  return (
    <SuggestedAgendaItemPanelContext.Provider value={contextValue}>
      {children}
      <SuggestedAgendaItemsPanel />
    </SuggestedAgendaItemPanelContext.Provider>
  );
};
